import { Link } from 'react-router-dom';
import { InformationCircleIcon } from '@heroicons/react/24/outline';

/**
 * Shared disclaimer for all programmatic SEO pages (/learn/*, /checklists/*, /states/*).
 *
 * Makes the informational scope explicit to readers and to search engines, and
 * anchors every page to the Terms of Service. For state pages, an authoritative
 * .gov primary source can be passed via `primarySourceUrl` to reinforce the
 * "verify with your state agency" message.
 *
 * Do not remove without reading memory/project_seo_foundation_state.md and the
 * pSEO plan at .claude/plans/silly-dreaming-knuth.md.
 */
interface PSEODisclaimerProps {
  lastUpdated?: string;
  primarySourceUrl?: string;
  primarySourceLabel?: string;
  className?: string;
}

export default function PSEODisclaimer({
  lastUpdated,
  primarySourceUrl,
  primarySourceLabel,
  className = '',
}: PSEODisclaimerProps) {
  const formattedDate = lastUpdated
    ? new Date(lastUpdated).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : null;

  return (
    <aside
      className={`rounded-lg border border-border bg-muted/40 p-4 md:p-5 text-sm text-muted-foreground ${className}`}
      role="note"
      aria-label="Content disclaimer"
    >
      <div className="flex items-start gap-3">
        <InformationCircleIcon className="h-5 w-5 shrink-0 mt-0.5 text-foreground/70" aria-hidden="true" />
        <div className="space-y-2 leading-relaxed">
          <p>
            <strong className="text-foreground">About this information.</strong>{' '}
            This page is for general informational purposes only. Programs, eligibility rules,
            and benefit amounts change. Verify current details with the relevant agency before
            making decisions.
            {formattedDate && (
              <>
                {' '}Last reviewed <span className="text-foreground">{formattedDate}</span>.
              </>
            )}
          </p>
          <p>
            SeniorThrive does not provide legal, medical, or financial advice. For advice about
            your specific situation, consult a licensed professional.{' '}
            {primarySourceUrl && (
              <>
                For the authoritative source, visit{' '}
                <a
                  href={primarySourceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary underline hover:no-underline"
                >
                  {primarySourceLabel || primarySourceUrl}
                </a>
                .{' '}
              </>
            )}
            See our{' '}
            <Link to="/terms" className="text-primary underline hover:no-underline">
              Terms of Service
            </Link>{' '}
            for more.
          </p>
        </div>
      </div>
    </aside>
  );
}
